import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Plus, X } from "lucide-react";
import { Badge, Button, Input } from "@runcheetah/ui";

type SaveState = "idle" | "saving" | "saved" | "error";

export type EditableTagListProps = {
  label: string;
  description?: string;
  tags: string[];
  onSave: (tags: string[]) => Promise<unknown> | unknown;
  placeholder?: string;
  emptyStateLabel?: string;
};

export function EditableTagList({
  label,
  description,
  tags,
  onSave,
  placeholder,
  emptyStateLabel,
}: EditableTagListProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const persist = async (nextTags: string[]) => {
    setSaveState("saving");

    try {
      await onSave(nextTags);
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  };

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || tags.includes(trimmed)) {
      setInputValue("");
      return;
    }

    void persist([...tags, trimmed]);
    setInputValue("");
  };

  const removeTag = (tag: string) => {
    void persist(tags.filter((item) => item !== tag));
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description ? (
          <p className="text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Badge key={tag} variant="outline" className="gap-1 rounded-full px-3 py-1 font-normal">
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="text-muted-foreground transition-colors hover:text-foreground"
              aria-label={intl.formatMessage(
                { id: "editableTagList.removeAria", defaultMessage: "Remove {tag}" },
                { tag },
              )}
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        {!tags.length ? (
          <p className="text-sm text-muted-foreground">
            {emptyStateLabel ?? (
              <FormattedMessage id="editableTagList.empty" defaultMessage="No items yet." />
            )}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addTag();
            }
          }}
          placeholder={
            placeholder ??
            intl.formatMessage({
              id: "editableTagList.placeholder",
              defaultMessage: "Add a word or phrase",
            })
          }
          className="max-w-xs"
        />
        <Button type="button" variant="outline" onClick={addTag} disabled={saveState === "saving"}>
          <Plus className="mr-1 h-4 w-4" />
          <FormattedMessage id="common.add" defaultMessage="Add" />
        </Button>
        {saveState !== "idle" ? (
          <p className="text-xs text-muted-foreground">
            {saveState === "saving" ? (
              <FormattedMessage id="common.saving" defaultMessage="Saving..." />
            ) : null}
            {saveState === "saved" ? (
              <FormattedMessage id="common.saved" defaultMessage="Saved" />
            ) : null}
            {saveState === "error" ? (
              <FormattedMessage
                id="common.saveErrorTryAgain"
                defaultMessage="Could not save. Try again."
              />
            ) : null}
          </p>
        ) : null}
      </div>
    </div>
  );
}
